import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, FileText, Download, Loader2, Upload, CheckCircle2, RefreshCw, Trash2 } from "lucide-react";
import api, { API, formatApiError } from "../lib/api";
import { formatFCFA, formatDate, formatDateTime } from "../lib/format";
import { PageHeader, Spinner, StatusBadge, MethodBadge, EmptyState } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const PROOF_REQUIRED = ["wave", "orange_money", "bank_transfer"];

function RecordPayment({ invoice, onDone }) {
  const [form, setForm] = useState({ amount: "", method: "wave", reference: "", installment_index: "", note: "" });
  const [fileId, setFileId] = useState(null);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const needProof = PROOF_REQUIRED.includes(form.method);
  const isPdf = (fileName || "").toLowerCase().endsWith(".pdf");

  const fileInputRef = React.useRef(null);

  const resetInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeProof = async () => {
    const current = fileId;
    setFileId(null);
    setFileName("");
    resetInput();
    if (current) {
      try {
        await api.delete(`/files/${current}`);
      } catch (err) {
        /* orphan cleanup best-effort */
      }
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const MAX = 5 * 1024 * 1024;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "pdf"].includes(ext)) {
      toast.error("Format non supporte. Utilisez JPG, PNG ou PDF.");
      resetInput();
      return;
    }
    if (file.size > MAX) {
      toast.error("Le justificatif depasse la taille maximale autorisee (5 Mo).");
      resetInput();
      return;
    }
    const previous = fileId;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(data.file_id);
      setFileName(data.filename);
      toast.success(previous ? "Justificatif remplace" : "Justificatif televerse");
      // clean up the replaced temporary file
      if (previous) {
        api.delete(`/files/${previous}`).catch(() => {});
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      resetInput();
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/payments", {
        invoice_id: invoice.id,
        amount: Number(form.amount),
        method: form.method,
        reference: form.reference || null,
        proof_file_id: needProof ? fileId : null,
        installment_index: form.installment_index === "" ? null : Number(form.installment_index),
        note: form.note || null,
      });
      toast.success("Paiement enregistre — en attente de validation de l'Administrateur Principal");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
      <p className="font-semibold text-slate-800 text-sm">Enregistrer un paiement</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Montant (FCFA)</Label><Input data-testid="pay-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 bg-white" /></div>
        <div>
          <Label>Methode</Label>
          <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
            <SelectTrigger data-testid="pay-method" className="mt-1.5 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white z-50">
              <SelectItem value="wave">Wave</SelectItem>
              <SelectItem value="orange_money">Orange Money</SelectItem>
              <SelectItem value="bank_transfer">Virement bancaire</SelectItem>
              <SelectItem value="cash">Especes</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {invoice.payment_type === "installment" && invoice.installments?.length > 0 && (
        <div>
          <Label>Tranche (optionnel)</Label>
          <Select value={form.installment_index} onValueChange={(v) => setForm({ ...form, installment_index: v })}>
            <SelectTrigger className="mt-1.5 bg-white"><SelectValue placeholder="Aucune tranche precise" /></SelectTrigger>
            <SelectContent className="bg-white z-50">
              {invoice.installments.map((ins) => <SelectItem key={ins.index} value={String(ins.index)}>{ins.label} — {formatFCFA(ins.amount)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div><Label>Reference transaction (optionnel)</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5 bg-white" placeholder="N° Wave / OM / virement" /></div>
      {needProof ? (
        <div>
          <Label>Justificatif (obligatoire)</Label>
          {fileId ? (
            <div data-testid="proof-uploaded" className="mt-1.5 rounded-lg border border-emerald-200 bg-emerald-50 p-2 space-y-2">
              <div data-testid="proof-preview" className="rounded-md overflow-hidden border border-emerald-100 bg-white">
                {isPdf ? (
                  <iframe title="Apercu du justificatif" src={`${API}/files/${fileId}#toolbar=0`} className="w-full h-52" />
                ) : (
                  <img src={`${API}/files/${fileId}`} alt="Apercu du justificatif" className="w-full max-h-56 object-contain" />
                )}
              </div>
              <div className="flex items-center justify-between gap-2 px-1 text-sm">
                <span className="flex items-center gap-2 min-w-0 text-emerald-800">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  <span className="truncate">{fileName}</span>
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    data-testid="pay-proof-replace"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-white"
                  >
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Remplacer
                  </button>
                  <button
                    type="button"
                    data-testid="pay-proof-remove"
                    onClick={removeProof}
                    disabled={uploading}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-rose-600 hover:bg-white"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              data-testid="pay-proof-trigger"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="mt-1.5 w-full flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-500 hover:border-teal-400 disabled:opacity-60"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Televersement..." : "Televerser une image ou un PDF"}
            </button>
          )}
          <input
            ref={fileInputRef}
            data-testid="pay-proof"
            type="file"
            accept="image/png,image/jpeg,application/pdf,.png,.jpg,.jpeg,.pdf"
            className="hidden"
            onChange={upload}
          />
        </div>
      ) : (
        <p data-testid="internal-receipt" className="text-xs text-emerald-700 bg-emerald-50 rounded-md px-3 py-2">Un recu interne numerote sera genere automatiquement pour ce paiement en especes.</p>
      )}
      <Button
        data-testid="pay-submit"
        onClick={submit}
        disabled={saving || uploading || !form.amount || (needProof && !fileId)}
        className="w-full bg-teal-600 hover:bg-teal-700"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Soumettre le paiement"}
      </Button>
    </div>
  );
}

function InvoiceDialog({ invoiceId, onClose, onChanged }) {
  const [inv, setInv] = useState(null);
  const load = useCallback(() => {
    if (invoiceId) api.get(`/invoices/${invoiceId}`).then((r) => setInv(r.data)).catch(() => {});
  }, [invoiceId]);
  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open={!!invoiceId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-4">
            <span className="font-mono">{inv ? inv.number : "Facture"}</span>
            {inv && <StatusBadge status={inv.status} />}
          </DialogTitle>
          <DialogDescription>Detail de la facture, echeancier et enregistrement des paiements.</DialogDescription>
        </DialogHeader>
        {!inv ? <Spinner /> : (
          <>
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{inv.customer?.name}</p>
                  <p className="text-sm text-slate-500">Abonnement {inv.plan_name} — Exercice {inv.year}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => window.open(`${API}/invoices/${inv.id}/pdf`, "_blank")} data-testid="invoice-pdf">
                  <Download className="h-4 w-4 mr-1.5" /> PDF
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">Total</p><p className="font-mono font-bold text-slate-900">{formatFCFA(inv.total_amount)}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">Regle</p><p className="font-mono font-bold text-emerald-600">{formatFCFA(inv.amount_paid)}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">Solde</p><p className="font-mono font-bold text-teal-600">{formatFCFA(inv.balance)}</p></div>
              </div>

              {inv.payment_type === "installment" && inv.installments?.length > 0 && (
                <div>
                  <p className="font-semibold text-slate-800 text-sm mb-2">Echeancier</p>
                  <div className="space-y-1.5">
                    {inv.installments.map((ins) => (
                      <div key={ins.index} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                        <span className="text-slate-700">{ins.label}</span>
                        <span className="text-slate-400">Echeance {formatDate(ins.due_date)}</span>
                        <span className="font-mono">{formatFCFA(ins.amount)}</span>
                        <StatusBadge status={ins.status === "pending" ? "sent" : ins.status} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="font-semibold text-slate-800 text-sm mb-2">Paiements</p>
                {inv.payments?.length === 0 && <p className="text-sm text-slate-400">Aucun paiement enregistre.</p>}
                <div className="space-y-1.5">
                  {inv.payments?.map((p) => (
                    <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                      <div className="flex items-center gap-2"><MethodBadge method={p.method} /><span className="font-mono">{formatFCFA(p.amount)}</span></div>
                      <span className="text-xs text-slate-400">{formatDate(p.created_at)}</span>
                      <StatusBadge status={p.status} />
                    </div>
                  ))}
                </div>
              </div>

              {inv.balance > 0 && <RecordPayment invoice={inv} onDone={() => { load(); onChanged(); }} />}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Invoices() {
  const { user } = useAuth();
  const isCentral = ["principal_admin", "finance_agent"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [openId, setOpenId] = useState(params.get("open") || null);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ customer_id: "", plan: "starter", year: new Date().getFullYear(), payment_type: "full", installment_count: 3 });

  const load = () => api.get("/invoices").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const closeDialog = () => { setOpenId(null); params.delete("open"); setParams(params); };

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/invoices", { ...form, year: Number(form.year), installment_count: Number(form.installment_count) });
      toast.success("Facture emise");
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!items) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="Factures annuelles"
        subtitle="Factures d'abonnement en FCFA, paiement integral ou echelonne, avec suivi du solde."
        actions={isCentral && (
          <Button data-testid="add-invoice-btn" onClick={() => setCreateOpen(true)} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4 mr-1.5" /> Nouvelle facture
          </Button>
        )}
      />

      <Card className="p-0 overflow-hidden">
        {items.length === 0 ? (
          <EmptyState icon={FileText} title="Aucune facture" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="px-6 py-4">N° Facture</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Solde</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id} data-testid={`table-row-${i.id}`} className="cursor-pointer" onClick={() => setOpenId(i.id)}>
                  <TableCell className="px-6 py-4 font-mono text-sm font-medium">{i.number}</TableCell>
                  <TableCell className="font-medium text-slate-800">{i.customer_name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{i.payment_type === "installment" ? `Echelonne (${i.installments?.length})` : "Integral"}</TableCell>
                  <TableCell className="text-right font-mono">{formatFCFA(i.total_amount)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-teal-700">{formatFCFA(i.balance)}</TableCell>
                  <TableCell><StatusBadge status={i.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <InvoiceDialog invoiceId={openId} onClose={closeDialog} onChanged={load} />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Nouvelle facture annuelle</DialogTitle>
          <DialogDescription>Emettez une facture d'abonnement annuel en FCFA, au paiement integral ou echelonne.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Client</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="inv-customer" className="mt-1.5"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                <SelectContent className="bg-white z-50">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Formule</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger data-testid="inv-plan" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Exercice</Label>
                <Select value={String(form.year)} onValueChange={(v) => setForm({ ...form, year: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white z-50">{[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Mode de paiement</Label>
                <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                  <SelectTrigger data-testid="inv-paytype" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="full">Integral</SelectItem>
                    <SelectItem value="installment">Echelonne</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.payment_type === "installment" && (
                <div>
                  <Label>Nombre de tranches</Label>
                  <Select value={String(form.installment_count)} onValueChange={(v) => setForm({ ...form, installment_count: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white z-50">{[2, 3, 4, 6].map((n) => <SelectItem key={n} value={String(n)}>{n} tranches</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button data-testid="inv-save" onClick={create} disabled={saving || !form.customer_id} className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Emettre la facture"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
